import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'

interface DeleteWorkspaceDialogProps {
  open: boolean
  onClose: () => void
  workspaceName: string
  onConfirm: () => Promise<void>
}

export function DeleteWorkspaceDialog({
  open,
  onClose,
  workspaceName,
  onConfirm,
}: DeleteWorkspaceDialogProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)

  const confirmed = inputValue === workspaceName

  async function handleConfirm() {
    if (!confirmed) return
    setLoading(true)
    try {
      await onConfirm()
    } finally {
      setLoading(false)
      setInputValue('')
    }
  }

  function handleClose() {
    setInputValue('')
    onClose()
  }

  return (
    <Dialog
      open={open}
      title={t('workspace.deleteDialog.title')}
      onClose={handleClose}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleConfirm()}
            disabled={!confirmed || loading}
          >
            {loading ? t('common.waiting') : t('workspace.deleteDialog.confirm')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {t('workspace.deleteDialog.message')}
        </p>
        <p
          style={{
            margin: 0,
            fontFamily: 'monospace',
            background: 'var(--bg-app)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: 13,
            userSelect: 'none',
          }}
        >
          {workspaceName}
        </p>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={t('workspace.deleteDialog.placeholder')}
          className="morph-input"
          autoFocus
          disabled={loading}
        />
      </div>
    </Dialog>
  )
}
